import React, { useEffect, useRef } from 'react';
import { initAnalytics, track } from './track';
import { EVENT_CATALOG } from './events';

/**
 * Owns the two identities every event carries:
 *
 *   clientId  — per device, persisted in localStorage. Identifies anonymous
 *               visitors as distinct users.
 *   sessionId — per session, held in sessionStorage. A fresh one (new tab, or
 *               more than SESSION_IDLE_MS of inactivity) fires `session_start`,
 *               which is what the session counter on /stats counts.
 */

const CLIENT_ID_KEY = 'mashlom.analytics.clientId';
const SESSION_STORAGE_KEY = 'mashlom.analytics.session';
const SESSION_IDLE_MS = 30 * 60 * 1000;

const generateId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateClientId = (): string => {
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const next = generateId();
    localStorage.setItem(CLIENT_ID_KEY, next);
    return next;
  } catch {
    return generateId();
  }
};

interface SessionRecord {
  id: string;
  lastSeenAt: number;
}

const readSession = (): SessionRecord | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === 'string' && typeof parsed.lastSeenAt === 'number') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

const writeSession = (record: SessionRecord): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore
  }
};

const getOrCreateSessionId = (): { id: string; isNew: boolean } => {
  const now = Date.now();
  const existing = readSession();
  if (existing && now - existing.lastSeenAt < SESSION_IDLE_MS) {
    writeSession({ id: existing.id, lastSeenAt: now });
    return { id: existing.id, isNew: false };
  }
  const id = generateId();
  writeSession({ id, lastSeenAt: now });
  return { id, isNew: true };
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const startedRef = useRef(false);

  if (!startedRef.current) {
    startedRef.current = true;
    try {
      const clientId = getOrCreateClientId();
      const session = getOrCreateSessionId();
      initAnalytics({ clientId, sessionId: session.id });
      if (session.isNew) {
        track(EVENT_CATALOG.session_start);
      }
    } catch {
      // swallow
    }
  }

  // Any interaction keeps the session alive, so the idle timeout above only
  // trips after a genuine 30-minute gap.
  useEffect(() => {
    const touch = () => {
      try {
        const existing = readSession();
        if (existing) writeSession({ id: existing.id, lastSeenAt: Date.now() });
      } catch {
        // ignore
      }
    };
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('focus', touch);
    window.addEventListener('mousemove', touch, { passive: true });
    window.addEventListener('keydown', touch);
    return () => {
      window.removeEventListener('focus', touch);
      window.removeEventListener('mousemove', touch);
      window.removeEventListener('keydown', touch);
    };
  }, []);

  return <>{children}</>;
};
